import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Camera, Loader2, Send, Sparkles, Image as ImageIcon, X, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = ['Physics', 'Chemistry', 'Biology', 'Mathematics'];

const DoubtSolver = () => {
  const [quota, setQuota] = useState(null);
  const [subject, setSubject] = useState('Physics');
  const [text, setText] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [solution, setSolution] = useState('');
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const fetchQuota = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/doubt-solver/quota`); setQuota(data); }
    catch (e) { console.error('Failed to load doubt quota:', e); }
  };
  const fetchHistory = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/doubt-solver/history`); setHistory(data); }
    catch (e) { console.error('Failed to load doubt history:', e); }
  };
  useEffect(() => { fetchQuota(); fetchHistory(); }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Image too large. Max 4 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      setImagePreview(result);
      const base64 = String(result).split(',')[1] || '';
      setImageBase64(base64);
    };
    reader.readAsDataURL(f);
  };

  const clearImage = () => {
    setImageBase64(''); setImagePreview('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSolve = async () => {
    if (!imageBase64 && !text.trim()) { toast.error('Upload an image or type a question'); return; }
    setLoading(true);
    setSolution('');
    try {
      const { data } = await axios.post(`${API_URL}/api/doubt-solver/solve`, {
        image_base64: imageBase64 || undefined,
        question_text: text.trim() || undefined,
        subject,
      });
      setSolution(data.solution);
      setQuota(data.quota);
      fetchHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'DOUBT_LIMIT_REACHED') {
        toast.error(detail.message || 'Free limit reached');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to solve');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="doubt-solver-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Lightbulb className="w-4 h-4" /> AI Doubt Solver
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="doubt-solver-title">
            Snap a doubt. Get a solution.
          </h1>
          <p className="text-base text-[#6B7280] max-w-2xl mx-auto">
            Take a photo of any JEE/NEET question — Claude Opus walks you through the full step-by-step solution.
          </p>
          {quota && (
            <p className="mt-4 text-xs text-[#6B7280]" data-testid="doubt-quota-line">
              {quota.is_pro ? '✨ Pro · unlimited' : `Free tier: ${quota.used} / ${quota.limit} used today`}
              {' · '}
              {!quota.is_pro && <Link to="/upgrade" className="text-[#0EA5E9] font-bold hover:underline">Upgrade for unlimited</Link>}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Input column */}
          <div className="md:col-span-2 rounded-3xl p-6 bg-white border border-slate-100 shadow-sm space-y-4" data-testid="doubt-input-card">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">Subject</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SUBJECTS.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSubject(s)}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold transition border ${
                      subject === s
                        ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]'
                        : 'bg-white text-[#6B7280] border-slate-200 hover:border-[#0EA5E9]'
                    }`}
                    data-testid={`subject-${s.toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">Upload question (image)</label>
              {imagePreview ? (
                <div className="mt-2 relative rounded-2xl overflow-hidden border-2 border-slate-200" data-testid="doubt-image-preview">
                  <img src={imagePreview} alt="doubt" className="w-full max-h-64 object-contain bg-slate-50" />
                  <button onClick={clearImage} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80" data-testid="doubt-image-remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="mt-2 w-full p-6 rounded-2xl border-2 border-dashed border-slate-200 hover:border-[#0EA5E9] hover:bg-slate-50 transition text-center"
                  data-testid="doubt-upload-button"
                >
                  <Camera className="w-7 h-7 mx-auto text-[#9CA3AF] mb-2" />
                  <p className="text-sm text-[#6B7280]"><strong>Click to upload</strong> or drag a screenshot here</p>
                  <p className="text-xs text-[#9CA3AF] mt-1">PNG, JPG · max 4 MB</p>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="doubt-file-input" />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">Or type the question</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="e.g. A particle moves with velocity v = 3t² + 2t. Find acceleration at t = 2s."
                className="mt-2 w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] outline-none resize-none"
                data-testid="doubt-text-input"
              />
            </div>

            <button
              onClick={handleSolve}
              disabled={loading || (!imageBase64 && !text.trim())}
              className="w-full px-6 py-4 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="doubt-solve-button"
            >
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Thinking…</> : <><Sparkles className="w-5 h-5" /> Solve my doubt</>}
            </button>
          </div>

          {/* History sidebar */}
          <div className="rounded-3xl p-5 bg-white border border-slate-100 shadow-sm" data-testid="doubt-history-card">
            <h3 className="font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <ImageIcon className="w-4 h-4" /> Recent doubts
            </h3>
            {history.length === 0 ? (
              <p className="text-xs text-[#9CA3AF]">No past doubts yet.</p>
            ) : (
              <ul className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((h, i) => (
                  <li key={i} className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                    <p className="font-bold text-[#111827] mb-0.5">{h.subject}</p>
                    <p className="text-[#6B7280] line-clamp-2">{h.question_text || (h.had_image ? '[image]' : '')}</p>
                    <p className="text-[10px] text-[#9CA3AF] mt-1">{new Date(h.created_at).toLocaleString()}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Solution */}
        {solution && (
          <div className="mt-8 rounded-3xl p-6 bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 shadow-sm" data-testid="doubt-solution-card">
            <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Sparkles className="w-5 h-5" /> Step-by-step solution
            </h3>
            <pre className="whitespace-pre-wrap text-sm text-[#111827] font-sans leading-relaxed" data-testid="doubt-solution-text">
              {solution}
            </pre>
          </div>
        )}
      </main>
    </div>
  );
};

export default DoubtSolver;
